import React, { useEffect, useState } from 'react';
import usePagination from "src/customHooks/usePagination";
import { CoachRecording } from "../types/coach-recording.entity";
import { useCountRecordsQuery, useGetRecordsQuery, useUpdateCoachApplicationStatusMutation } from "../coach-recordings.service";
import PageHeader from "src/components/pageHeader";
import Pagination from "src/components/pagination";
import { Navigate } from "react-router-dom";
import PageLoading from "src/components/pageLoading";
import CoachRecordingCards from "../components/coach-recording-cards";
import { CoachRecordingFilter } from "../components/coach-recording-filter";
import { CoachRecordingsSkeleton } from "../components/CoachRecordingsSkeleton";
import { useAppDispatch } from "src/store";
import { showToast } from "src/store/toast.store";
import { FaArrowUp } from "react-icons/fa";
// @ts-ignore - sweetalert2 has no types in current setup
import Swal from 'sweetalert2';

const CoachRecordings = () => {
  const { data, numberOfPages, currentPage, next, prev, isSuccess, isError, isLoading, isFetching, updateQuery } =
    usePagination<CoachRecording>({
      useGetDataQuery: useGetRecordsQuery,
      useCountData: useCountRecordsQuery,
    });
  
  const dispatch = useAppDispatch();
  const [updateStatus] = useUpdateCoachApplicationStatusMutation();
  const [selectedRecords, setSelectedRecords] = useState<string[]>([]);
  const [showScrollTop, setShowScrollTop] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 300);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  const handleStatusUpdate = async (action: "APPROVE" | "REJECT") => {
    if (selectedRecords.length === 0) {
      Swal.fire({
        title: 'No Records Selected',
        text: 'Please select at least one record to perform this action.',
        icon: 'warning',
        confirmButtonColor: '#3b82f6'
      });
      return;
    }

    const actionText = action === "APPROVE" ? "approve" : "reject";
    const actionTextPast = action === "APPROVE" ? "approved" : "rejected";
    
    // Show confirmation dialog
    const result = await Swal.fire({
      title: `Confirm Bulk ${actionText}`,
      text: `Are you sure you want to ${actionText} ${selectedRecords.length} selected coach application(s)?`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: action === "APPROVE" ? '#10b981' : '#ef4444',
      cancelButtonColor: '#6b7280',
      confirmButtonText: `Yes, ${actionText} ${selectedRecords.length}`,
      cancelButtonText: 'Cancel'
    });

    if (result.isConfirmed) {
      try {
        // Show loading state
        Swal.fire({
          title: `${actionText.charAt(0).toUpperCase() + actionText.slice(1)}ing...`,
          text: `Please wait while we ${actionText} ${selectedRecords.length} coach application(s)...`,
          allowOutsideClick: false,
          didOpen: () => {
            Swal.showLoading();
          }
        });

        // Process all selected records
        const promises = selectedRecords.map(recordId => 
          updateStatus({ id: recordId, action }).unwrap()
        );
        
        await Promise.all(promises);
        
        // Show success message
        Swal.fire({
          title: 'Success! 🎉',
          text: `${selectedRecords.length} coach application(s) ${actionTextPast} successfully`,
          icon: 'success',
          confirmButtonColor: '#3b82f6'
        });
        
        // Clear selection and show toast notification
        setSelectedRecords([]);
        dispatch(
          showToast({
            type: "success",
            message: `${selectedRecords.length} coach(s) ${actionTextPast} successfully`,
          })
        );
      } catch (error) {
        // Show error message
        Swal.fire({
          title: 'Error! ❌',
          text: `Failed to ${actionText} some coach applications. Please try again.`,
          icon: 'error',
          confirmButtonColor: '#ef4444'
        });
        
        // Also show toast notification
        dispatch(
          showToast({
            type: "error",
            message: "Failed to update some statuses",
          })
        );
      }
    }
  };

  const handleSelectAll = () => {
    if (!data) return;
    
    // Only select PENDING and UNDER_REVIEW records
    const selectableRecords = data.filter(record => 
      record.manualReviewStatus === "PENDING" || record.manualReviewStatus === "UNDER_REVIEW"
    );
    
    if (selectedRecords.length === selectableRecords.length) {
      setSelectedRecords([]);
    } else {
      setSelectedRecords(selectableRecords.map(record => record.id));
    }
  };

  const handleSelectRecord = (recordId: string) => {
    setSelectedRecords((prev: string[]) => 
      prev.includes(recordId) 
        ? prev.filter((id: string) => id !== recordId)
        : [...prev, recordId]
    );
  };

  return (
    <div className="page_container">
      <PageHeader text="Coaches Applications" />

      <CoachRecordingFilter updateFilterQuery={(where: Partial<CoachRecording>) => updateQuery(where)} />

      {isSuccess ? (
        <>
          {/* Bulk Actions Section */}
          <div className="bg-gradient-to-br from-white via-blue-50/30 to-indigo-50/50 rounded-2xl shadow-lg p-6 border border-gray-200/60 mb-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Bulk Actions</h3>
              <div className="flex items-center space-x-3">
                <button
                  onClick={handleSelectAll}
                  className="px-4 py-2 bg-gradient-to-r from-gray-500 to-gray-600 hover:from-gray-600 hover:to-gray-700 text-white rounded-xl transition-all duration-300 font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-1 hover:scale-105"
                >
                  {selectedRecords.length === (data?.filter(record => 
                    record.manualReviewStatus === "PENDING" || record.manualReviewStatus === "UNDER_REVIEW"
                  ).length || 0) ? 'Deselect All' : 'Select All'}
                </button>
                <span className="text-sm text-gray-600">
                  {selectedRecords.length} of {data?.filter(record => 
                    record.manualReviewStatus === "PENDING" || record.manualReviewStatus === "UNDER_REVIEW"
                  ).length || 0} selectable
                </span>
              </div>
            </div>
            
            <div className="flex items-center space-x-4">
              <button
                onClick={() => handleStatusUpdate("APPROVE")}
                disabled={selectedRecords.length === 0}
                className="px-6 py-3 bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 disabled:from-gray-400 disabled:to-gray-500 disabled:cursor-not-allowed text-white rounded-xl transition-all duration-300 font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-1 hover:scale-105 flex items-center space-x-2"
                title="Approve Selected Coaches"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
                <span>Approve Selected ({selectedRecords.length})</span>
              </button>
              
              <button
                onClick={() => handleStatusUpdate("REJECT")}
                disabled={selectedRecords.length === 0}
                className="px-6 py-3 bg-gradient-to-r from-red-500 to-pink-600 hover:from-red-600 hover:to-pink-700 disabled:from-gray-400 disabled:to-gray-500 disabled:cursor-not-allowed text-white rounded-xl transition-all duration-300 font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-1 hover:scale-105 flex items-center space-x-2"
                title="Reject Selected Coaches"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
                <span>Reject Selected ({selectedRecords.length})</span>
              </button>
            </div>
          </div>

          {isLoading || isFetching ? (
            <CoachRecordingsSkeleton rows={6} />
          ) : (
            <CoachRecordingCards data={data} onSelectRecord={handleSelectRecord} selectedRecords={selectedRecords} />
          )}
          <Pagination numberOfPages={numberOfPages} currentPage={currentPage} prev={prev} next={next} isLoading={isLoading || isFetching} />
        </>
      ) : isError ? (
        <Navigate to="/505" />
      ) : (
        <PageLoading />
      )}

      {showScrollTop && (
        <button
          onClick={scrollToTop}
          className="fixed bottom-8 right-8 bg-emerald-600 hover:bg-emerald-700 text-white p-1.5 rounded-full shadow-2xl ring-4 ring-amber-300/80 ring-offset-2 ring-offset-white transition-all duration-300 z-50 group"
          aria-label="Scroll to top"
        >
          <div className="flex items-center justify-center rounded-full border-2 border-amber-100/80 px-3 py-3">
            <FaArrowUp className="text-xl group-hover:-translate-y-1 transition-transform duration-300" />
          </div>
        </button>
      )}
    </div>
  );
};

export default CoachRecordings;
